'use client';

import {
	ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useReducer,
	useRef,
} from 'react';
import { ZERO } from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import { useQueryClient } from '@tanstack/react-query';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import { getLiveMarkPrice } from 'src/stores/marketLiveState';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { accountTradingDataQueryKeys } from 'src/hooks/query/accountTradingData/accountTradingDataQueryKeys';
import { useMarginModeSync } from './useMarginModeSync';
import AccountTradingDataSync from './AccountTradingDataSync';
import { useUserAccountFreshnessWatchdog } from './useUserAccountFreshnessWatchdog';

const AccountTradingDataContext = createContext<boolean>(false);

/**
 * `useLazySubAccounts` only re-renders when the SET of subaccount keys changes,
 * so a `User` that dies (or revives) mid-session would never remount its sync.
 * This tick re-evaluates `isSubscribed` regardless. It is a render of
 * null-returning components — no RPC, no work in the children tree.
 */
const SUBSCRIPTION_HEALTH_TICK_MS = 5_000;

export function useAccountTradingDataContext(): boolean {
	return useContext(AccountTradingDataContext);
}

/**
 * Orchestrates per-account trading data sync.
 * Separated from the provider so its frequent re-renders
 * (from useLazySubAccounts, useMarketStateStore, etc.) don't
 * cascade into the children tree.
 */
function AccountTradingDataSyncOrchestrator() {
	const accounts = useLazySubAccounts();
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const perpMarketsLookup = usePerpMarketsLookup();
	const connected = useWalletIsConnected();
	const queryClient = useQueryClient();

	const markPriceCallback = useCallback((marketIndex: number) => {
		return (
			getLiveMarkPrice(MarketId.createPerpMarket(marketIndex))?.val ?? ZERO
		);
	}, []);

	// Margin mode sync for current account
	useMarginModeSync();

	const shouldSync =
		connected &&
		velocityClientIsReady &&
		!!velocityClient &&
		perpMarketsLookup.length > 0;

	// Clear stale cache when sync stops (wallet disconnect, etc.)
	const prevShouldSync = useRef(false);
	useEffect(() => {
		if (prevShouldSync.current && !shouldSync) {
			queryClient.removeQueries({
				queryKey: accountTradingDataQueryKeys.marginInfo(),
			});
			queryClient.removeQueries({
				queryKey: accountTradingDataQueryKeys.positions(),
			});
			queryClient.removeQueries({
				queryKey: accountTradingDataQueryKeys.orders(),
			});
			queryClient.removeQueries({
				queryKey: accountTradingDataQueryKeys.leftoverIsolatedPositions(),
			});
		}
		prevShouldSync.current = shouldSync;
	}, [shouldSync, queryClient]);

	const [, reevaluateSubscriptionHealth] = useReducer((n: number) => n + 1, 0);
	useEffect(() => {
		const interval = setInterval(
			() => reevaluateSubscriptionHealth(),
			SUBSCRIPTION_HEALTH_TICK_MS
		);
		return () => clearInterval(interval);
	}, []);

	// Mounted here (the single mount point) so it sees every subaccount,
	// including the unsubscribed ones the sync components skip.
	useUserAccountFreshnessWatchdog(accounts, shouldSync);

	if (!shouldSync) return null;

	return (
		<>
			{accounts.map((acct) => {
				if (!acct.client?.isSubscribed) return null;
				return (
					<AccountTradingDataSync
						key={acct.userKey}
						userKey={acct.userKey}
						user={acct.client}
						velocityClient={velocityClient}
						perpMarketsLookup={perpMarketsLookup}
						markPriceCallback={markPriceCallback}
					/>
				);
			})}
		</>
	);
}

/**
 * Provider that marks the account trading data system as mounted.
 * Consumer hooks use this context to warn in dev when the provider is missing.
 *
 * The sync orchestrator is a sibling of {children} so its re-renders
 * don't propagate to the rest of the app tree.
 *
 * Mounted in AppWrapper's memoizedProviders array.
 */
export default function AccountTradingDataProvider({
	children,
}: {
	children?: ReactNode;
}) {
	return (
		<AccountTradingDataContext.Provider value={true}>
			<AccountTradingDataSyncOrchestrator />
			{children}
		</AccountTradingDataContext.Provider>
	);
}
